/**
 * ONE MESSAGES ROW PER COMP BOARD POST, HOWEVER MANY REPLIES IT HAS HAD.
 *
 * Live report, 2026-09-24 4:45pm Central, a comp parent on her phone: "It's
 * consistently adding notifications but I've marked everything done. They
 * keep coming back up." Since 9/23 every Comp Team board reply mints its own
 * bell card for every comp family (notificationAuthority.mjs's compBoard
 * lane, one deterministic card per reply per person). Messages rendered each
 * card as its own "<author> on the comp board" row showing the PARENT post,
 * and Done cleared only the one card she tapped, so the next reply brought
 * back a row identical to the one she had just finished.
 *
 * Now every card for one post, in one state, is one row: the newest card
 * stands for the group (its id, time and snooze), and Done on it clears the
 * whole pile (Messages.tsx handleFileNotif). "One state" matters: a card she
 * already filed and a reply that arrived afterwards are different things, so
 * filed and unfiled cards for the same post never share a row. A reply that
 * lands after Done is a fresh row of its own, which is the point.
 *
 * Every other card passes through as a group of one, unchanged. Pure on
 * purpose, so node --test reaches it without React or Firebase.
 */

export interface GroupableNotification {
  id: string;
  compBoardPostId?: string;
  compBoardReplyId?: string;
  dismissed?: boolean;
  createdAt?: unknown;
}

export interface NotifInboxGroup<T> {
  /** The newest card: the row's id, time, snooze and Done target. */
  representative: T;
  /** Every card in the group, newest first. A single card for anything else. */
  cards: T[];
  /** How many of the cards are for a reply rather than the post itself. */
  replyCount: number;
  /** The card for the post itself, when it is in the group. */
  postCard: T | null;
}

function createdMs(value: unknown): number {
  // A server timestamp still in flight is the newest thing there is.
  if (value === null || value === undefined) return Number.POSITIVE_INFINITY;
  if (typeof value !== 'object') return 0;
  const toMillis = (value as { toMillis?: unknown }).toMillis;
  if (typeof toMillis !== 'function') return Number.POSITIVE_INFINITY;
  const ms = toMillis.call(value);
  return typeof ms === 'number' && Number.isFinite(ms) ? ms : 0;
}

/** Group comp board cards by post and filed state. Input order is kept. */
export function groupNotificationsForInbox<T extends GroupableNotification>(
  notifications: readonly T[]
): NotifInboxGroup<T>[] {
  const groups: NotifInboxGroup<T>[] = [];
  const byKey = new Map<string, T[]>();
  const order: Array<{ key: string } | { single: T }> = [];
  for (const notification of notifications) {
    if (!notification.compBoardPostId) {
      order.push({ single: notification });
      continue;
    }
    const key = `${notification.compBoardPostId}\u001f${notification.dismissed === true ? 'filed' : 'open'}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.push(notification);
    } else {
      byKey.set(key, [notification]);
      order.push({ key });
    }
  }
  for (const entry of order) {
    if ('single' in entry) {
      groups.push({
        representative: entry.single,
        cards: [entry.single],
        replyCount: entry.single.compBoardReplyId ? 1 : 0,
        postCard: entry.single.compBoardPostId && !entry.single.compBoardReplyId ? entry.single : null,
      });
      continue;
    }
    const cards = [...(byKey.get(entry.key) ?? [])].sort(
      (a, b) => createdMs(b.createdAt) - createdMs(a.createdAt)
    );
    groups.push({
      representative: cards[0],
      cards,
      replyCount: cards.filter((card) => Boolean(card.compBoardReplyId)).length,
      postCard: cards.find((card) => !card.compBoardReplyId) ?? null,
    });
  }
  return groups;
}

function replyWord(count: number): string {
  return count === 1 ? '1 new reply' : `${count} new replies`;
}

/**
 * The small label above a comp board row: "Comp board", plus how many new
 * replies are behind it once there is more than one card, so a busy thread
 * reads as one post with news rather than a pile of separate things.
 */
export function compBoardGroupEyebrow(group: Pick<NotifInboxGroup<unknown>, 'cards' | 'replyCount'>): string {
  if (group.cards.length > 1 && group.replyCount > 0) return `Comp board, ${replyWord(group.replyCount)}`;
  return 'Comp board';
}

/**
 * The row title for a group made only of reply cards, when there is more
 * than one: "3 new replies on the comp board". Null means use the card's own
 * resolved title (the post, or a single reply's own stored title).
 */
export function compBoardGroupTitle(group: Pick<NotifInboxGroup<unknown>, 'cards' | 'replyCount' | 'postCard'>): string | null {
  if (group.postCard || group.replyCount < 2) return null;
  return `${replyWord(group.replyCount)} on the comp board`;
}
